import { MapPin, Briefcase, Cake } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { LEADERSHIP } from "@/constants/testIds";

const MEMBERS = [
  { key: "alexei", name: "Alexei Zotova", role: "founder", circle: null, location: "Lausanne", age: 22 },
  { key: "lucien", name: "Lucien C.", role: "leader", circle: "Bern", location: "Bern", age: 20 },
  { key: "tjorven", name: "Tjorven K.", role: "leader", circle: "Basel", location: "Basel", age: 27 },
  { key: "nadia", name: "Nadia I.", role: "leaderF", circle: "Lausanne", location: "VD", age: 24 },
  { key: "marco", name: "Marco S.", role: "deputy", circle: "Bern", location: "Bern", age: 24 },
  { key: "lars", name: "Lars B.", role: "deputy", circle: "Basel", location: "Basel", age: 27 },
  { key: "celin", name: "Celin G.", role: "deputy", circle: "VD", location: "VD", age: 19 },
];

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const MemberCard = ({ member, featured, index }) => {
  const { t } = useLanguage();
  const lead = t.leadership;
  const badge = lead.badges[member.role];
  const fn =
    member.role === "founder"
      ? lead.roles.founder
      : `${lead.roles[member.role]} ${member.circle}`;

  return (
    <motion.article
      data-testid={LEADERSHIP.card(member.key)}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      className={`group relative flex flex-col items-center text-center rounded-3xl border border-gold/15 bg-white/[0.03] backdrop-blur-sm transition-all duration-500 hover:border-gold/45 hover:bg-white/[0.06] ${
        featured ? "p-10 lg:p-12" : "p-8 lg:p-9"
      }`}
    >
      {/* Monogram */}
      <div
        className={`relative flex items-center justify-center rounded-full border border-gold/40 transition-transform duration-500 group-hover:scale-105 ${
          featured ? "w-28 h-28" : "w-20 h-20"
        }`}
        style={{ background: "radial-gradient(circle at 30% 25%, rgba(229,199,107,0.18), rgba(198,156,34,0.06))" }}
      >
        <span
          className={`font-accent italic font-medium leading-none ${featured ? "text-4xl" : "text-3xl"}`}
          style={{ color: "#E5C76B" }}
        >
          {initials(member.name)}
        </span>
      </div>

      {/* Badge */}
      <span
        className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-semibold"
        style={{ color: "#C69C22" }}
      >
        <span className="w-5 h-px bg-gold" />
        {badge}
      </span>

      {/* Name */}
      <h3 className={`mt-3 font-accent italic font-medium leading-none text-cream ${featured ? "text-4xl" : "text-3xl"}`}>
        {member.name}
      </h3>

      {/* Details */}
      <dl className="mt-6 w-full max-w-[15rem] space-y-2.5 text-sm">
        <div className="flex items-center justify-center gap-2 text-cream/80">
          <Briefcase size={14} style={{ color: "#E5C76B" }} className="flex-shrink-0" />
          <span>
            <span className="text-cream/50">{lead.labelFunction}: </span>
            {fn}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2 text-cream/80">
          <MapPin size={14} style={{ color: "#E5C76B" }} className="flex-shrink-0" />
          <span>
            <span className="text-cream/50">{lead.labelLocation}: </span>
            {member.location}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2 text-cream/80">
          <Cake size={14} style={{ color: "#E5C76B" }} className="flex-shrink-0" />
          <span>
            <span className="text-cream/50">{lead.labelAge}: </span>
            {member.age}
          </span>
        </div>
      </dl>
    </motion.article>
  );
};

const Leadership = () => {
  const { t } = useLanguage();
  const founder = MEMBERS.find((m) => m.role === "founder");
  const rest = MEMBERS.filter((m) => m.role !== "founder");

  return (
    <section
      id="leadership"
      data-testid="leadership-section"
      className="relative py-24 lg:py-36 bg-anthracite text-cream overflow-hidden"
    >
      <div
        className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[400px]"
        style={{ background: "radial-gradient(ellipse, rgba(198,156,34,0.12) 0%, transparent 60%)" }}
      />

      <div className="container-boe relative">
        <div className="max-w-2xl mx-auto text-center">
          <span className="section-eyebrow justify-center">{t.leadership.eyebrow}</span>
          <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold text-cream leading-[1.05] tracking-tight">
            {t.leadership.title}
          </h2>
          <p className="mt-6 text-lg text-cream/75">{t.leadership.subtitle}</p>
        </div>

        {/* Founder featured */}
        <div className="mt-16 flex justify-center">
          <div className="w-full max-w-sm">
            <MemberCard member={founder} featured index={0} />
          </div>
        </div>

        {/* Rest grid */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-7">
          {rest.map((m, i) => (
            <MemberCard key={m.key} member={m} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Leadership;
